import { z } from 'zod'
import { ViewOrderSchema } from '../view/order'
import { EntityIdSchema, WorkflowIdSchema, WorkflowStageIdSchema, type EntityId } from '../ids'
import { CloserModelSchema, SystemCloserStrategySchema } from '../company'
import { type AuditProps } from '../common'
import { CloserInteractionSchema, CloserSettingsSchema, CloserStatusSchema } from '../closer'
import { CountryCodeSchema } from '../address'

export const AREA_VISIBILITY_OPTIONS = ['All', 'Self', 'DirectTeam', 'ParentTeam'] as const
export const AreaVisibilityPreferenceSchema = z.enum(AREA_VISIBILITY_OPTIONS)
export type AreaVisibilityPreference = (typeof AREA_VISIBILITY_OPTIONS)[number]

export const DUPLICATE_ACCOUNTS_OPTIONS = ['Allow', 'Warn', 'Deny', 'Multiple', 'Auto'] as const
export const DuplicateAccountsPreferenceSchema = z.enum(DUPLICATE_ACCOUNTS_OPTIONS)
export type DuplicateAccountsPreference = (typeof DUPLICATE_ACCOUNTS_OPTIONS)[number]

export const DISPOSITION_MODE_OPTIONS = ['canvass', 'recorder', 'all'] as const
export const DispositionModeSchema = z.enum(DISPOSITION_MODE_OPTIONS)
export type DispositionMode = (typeof DISPOSITION_MODE_OPTIONS)[number]

export const STRICT_SUBAREA_OPTIONS = ['off', 'warn', 'block'] as const
export const StrictSubareaPreferenceSchema = z.enum(STRICT_SUBAREA_OPTIONS)
export type StrictSubareaPreference = (typeof STRICT_SUBAREA_OPTIONS)[number]

export const RECORDING_REMINDER_OPTIONS = ['Always', 'Daily', 'Never'] as const
export const RecordingReminderPreferenceSchema = z.enum(RECORDING_REMINDER_OPTIONS)
export type RecordingReminderPreference = (typeof RECORDING_REMINDER_OPTIONS)[number]

export const UNIT_SYSTEM_OPTIONS = ['imperial', 'metric'] as const
export const UnitSystemPreferenceSchema = z.enum(UNIT_SYSTEM_OPTIONS)
export type UnitSystemPreference = (typeof UNIT_SYSTEM_OPTIONS)[number]

export const HIDDEN_CANVASS_MAP_LAYER_OPTIONS = ['solar', 'customers', 'parcel boundaries', 'autoselect'] as const
export const HiddenCanvassMapLayerSchema = z.enum(HIDDEN_CANVASS_MAP_LAYER_OPTIONS)
export type HiddenCanvassMapLayer = (typeof HIDDEN_CANVASS_MAP_LAYER_OPTIONS)[number]

export const HIDDEN_CANVASS_UPSELL_OPTIONS = ['canvassPro', 'canvassMax'] as const
export const HiddenCanvassUpsellSchema = z.enum(HIDDEN_CANVASS_UPSELL_OPTIONS)
export type HiddenCanvassUpsell = (typeof HIDDEN_CANVASS_UPSELL_OPTIONS)[number]

export const LOCKABLE_USER_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'email',
  'title',
  'clientUserId',
  'startDate',
  'customFields',
  'birth',
  'mailingAddress',
  'language',
  'biography',
  'shirtSize',
  'shoeSize',
  'timeZone',
  'avatar',
] as const
export const LockableUserFieldSchema = z.enum(LOCKABLE_USER_FIELDS)
export type LockableUserField = (typeof LOCKABLE_USER_FIELDS)[number]

export const LOCKABLE_USER_FIELD_LABELS = new Map<LockableUserField, string>([
  ['firstName', 'First Name'],
  ['lastName', 'Last Name'],
  ['preferredName', 'Preferred Name'],
  ['email', 'Email'],
  ['title', 'Title'],
  ['clientUserId', 'External ID'],
  ['startDate', 'Start Date'],
  ['customFields', 'Custom Fields'],
  ['birth', 'Birthday'],
  ['mailingAddress', 'Mailing Address'],
  ['language', 'Language'],
  ['biography', 'Biography'],
  ['shirtSize', 'Shirt Size'],
  ['shoeSize', 'Shoe Size'],
  ['timeZone', 'Time Zone'],
  ['avatar', 'Avatar'],
])

export const LOCKABLE_TEAM_FIELDS = [
  'name',
  'externalId',
  'description',
  'customFields',
  'officeAddress',
  'avatar',
] as const
export const LockableTeamFieldSchema = z.enum(LOCKABLE_TEAM_FIELDS)
export type LockableTeamField = (typeof LOCKABLE_TEAM_FIELDS)[number]

export const LOCKABLE_TEAM_FIELD_LABELS = new Map<LockableTeamField, string>([
  ['name', 'Name'],
  ['externalId', 'External ID'],
  ['description', 'Description'],
  ['customFields', 'Custom Fields'],
  ['officeAddress', 'Office Address'],
  ['avatar', 'Avatar'],
])

export const PreferencesSchema = z.object({
  /**
   * @defaultValue 90
   * minimum seconds that must elapse between adding another pitch by same user
   * set to 0 for no limit
   */
  secondsBetweenPitches: z.number().optional(),
  /**
   * @defaultValue 10
   * minimum meters between all previous pitches by same user today
   * set to 0 for no limit
   */
  metersBetweenPitchesToday: z.number().optional(),
  /**
   * @defaultValue 1000
   * maximum meters from a location that the rep can be to count towards his activity
   * set to 0 for no limit
   */
  maxMetersFromLocationToActivity: z.number().optional(),
  /**
   * @defaultValue 250
   * the maximum distance in meters from a location that a pitch can be recorded
   * set to 0 for no limit
   */
  maxMetersFromLocationToPitch: z.number().optional(),
  /**
   * @defaultValue -1
   * minimum days that must elapse before a rep can pitch the same home again
   * set to -1 to disable this feature
   */
  daysBetweenPitch: z.number().optional(),
  /**
   * @defaultValue 0
   * maximum distance in meters from an event to the disposition location
   * set to 0 for no limit
   */
  maxMetersFromEventToDisposition: z.number().optional(),
  /**
   * @defaultValue false
   * before users contact, they must clock in
   */
  requireClockInBeforeContact: z.boolean().optional(),
  /** Is Enerflo integration enabled? */
  enerflo: z.boolean().optional(),
  /** Should commercial properties be shown on the map? */
  showCommercialProperties: z.boolean().optional(),
  /** Is enterprise login and user sync enabled?
   * This feature disables/prevents users from performing certain actions such as:
   * - Reset password
   * - Change email address
   * - Add and remove team members
   * - Archive and unarchive users
   */
  sso: z.boolean().optional(),
  /**
   * When enabled, the user will be prompted to enter whether a pitch was a phone or door pitch when pitching.
   */
  phonePitch: z.boolean().optional(),
  limitByArea: z.boolean().optional(),
  /** the default closer status */
  closerStatus: CloserStatusSchema.optional(),
  /**
   * undefined - disabled
   * setter - reps can choose a closer for the event
   * dispatcher - a dispatcher chooses the closer for all events
   * */
  closerModel: CloserModelSchema.optional(),
  /**
   * The strategy used to auto-assign closers when closerModel is 'system'.
   * { type: 'random' } - randomly pick from available closers
   * { type: 'balanced', windowDays?: number } - pick the closer with fewest events in windowDays (default 7)
   * { type: 'preferential', jitter: number } - pick by closer rank with jitter % chance of random (0-100)
   * @defaultValue { type: 'random' }
   */
  systemCloserStrategy: SystemCloserStrategySchema.optional(),
  /**
   * @defaultValue Full
   * Full - Stats are displayed for both the setter and closer and setters can schedule close events for closers
   * Partial - Only one set of stats are displayed and all users are selfgen but still have the ability to schedule close events and assign closers
   * None - Only one set of stats are displayed and all users are selfgen. There are no close events, they would only have comebacks.
   */
  closerInteraction: CloserInteractionSchema.optional(),
  /**
   * @description - When columns are selected, they will be displayed on the leaderboard. Otherwise, the leaderboard will display the 10 most popular columns.
   */
  leaderboardColumns: z.array(z.string()).optional(),
  /**
   * @description - When columns are selected, they will be used as the default columns for stats.
   */
  defaultStatsColumns: z.array(z.string()).optional(),
  /**
   * The percentage of closer appointments that can be booked for any given slot.
   * @defaultValue 100
   */
  bookingPercent: z.number().optional(),
  /** When enabled users will be locked out on required tasks */
  taskLockout: z.boolean().optional(),
  /** When enabled, users must submit their quarterly plan before accessing Canvass or Stats */
  planLockout: z.boolean().optional(),
  /**
   * When enabled setters will be restricted on creating close events based on closer timeslots and availability
   * @defaultValue false
   * */
  restrictSetterCloseEvents: z.boolean().optional(),
  /** When enabled, the mobile app locks Canvass until the rep has recorded at least 1 conversation (older app versions check for 1 evaluation instead) */
  requireEvalToCanvass: z.boolean().optional(),
  /** When enabled, skill evaluations will be automatically assigned to the manager of the user */
  autoAssignEvals: z.boolean().optional(),
  /**
   * Which area assignments can users view?
   * All - all areas
   * Self - only their own (default)
   * DirectTeam - only their own team
   * ParentTeam - for their own team and their parent team
   * */
  areaVisibility: AreaVisibilityPreferenceSchema.optional(),
  /**
   * @defaultValue 'Allow'
   * Allow - silently allow users to create duplicate accounts
   * Warn - warn users when creating duplicate accounts and require a confirmation
   */
  allowDuplicateAccounts: DuplicateAccountsPreferenceSchema.optional(),
  /**
   * Location data, geocoding and location matching will be based on this country
   * @defaultValue US
   */
  country: CountryCodeSchema.optional(),
  /**
   * When enabled, users cannot edit a custom location's address.
   * Canvass matches the address and creates the location without an address editor.
   * @defaultValue false
   */
  blockCustomLocationDetailEdits: z.boolean().optional(),
  /**
   * The maximum minutes after an event creation that a setter can delete the event
   * @defaultValue 60
   */
  maxMinutesSetterCanDeleteEvent: z.number().optional(),
  /**
   * The number of days between interviews before a manager is prompted to schedule another interview
   * @defaultValue 90
   */
  daysBetweenInterview: z.number().optional(),
  /**
   * The number of days between shadowing before a rep is prompted to schedule another shadow
   * @defaultValue 90
   */
  daysBetweenShadow: z.number().optional(),
  /** When enabled, multi dwelling units are displayed on the map */
  showMultiUnitDwellings: z.boolean().optional(),
  /** Hide pins without sales activity */
  hideInactivePins: z.boolean().optional(),
  /** When enabled, visibility can be restricted within a team */
  enableVisibilityRestrictions: z.boolean().optional(),
  /** length of the event meetings in minutes */
  meetingDuration: z.number().optional(),
  /** The minimum time in minutes between appointments */
  timeBetweenAppointments: z.number().optional(),
  closerSettings: CloserSettingsSchema.pick({
    timeslots: true,
    excludes: true,
    includes: true,
  }).optional(),
  /**
   * Whether to require a lead resident's information before creating a lead
   * @defaultValue true
   * @deprecated use requiredFields in accountStatus
   */
  requireLeadResidentInfo: z.boolean().optional(),
  /**
   * Whether a user can be in multiple teams
   * @defaultValue false
   */
  canBeInMultipleTeams: z.boolean().optional(),
  /**
   * When enabled, users will see a simplified version of team management built around managers.
   * @defaultValue false
   */
  managerBasedTeams: z.boolean().optional(),
  /**
   * How many days between asking users to confirm who they report to.
   * Set to -1 to disable.
   * @defaultValue -1
   */
  daysBetweenAskManager: z.number().optional(),
  dispositionMode: DispositionModeSchema.optional(),
  /**
   * How many days to keep recorded conversations for before expiring
   * Set to 0 to disable expiration
   * @defaultValue 0
   */
  conversationExpiration: z.int().nonnegative().optional(),
  /**
   * Allow company to operate in multiple languages
   * @defaultValue false
   */
  multiLanguage: z.boolean().optional(),
  /**
   * Number of days of inactivity before a user is automatically archived
   * @defaultValue 0 (disabled)
   */
  inactiveDaysBeforeArchive: z.number().optional(),
  enableAccountManagement: z.boolean().optional(),
  enableAreaManagement: z.boolean().optional(),
  strictSubareaAssignment: StrictSubareaPreferenceSchema.optional(),
  conversationLocationUpdateThreshold: z.number().optional(),
  /**
   * How often to remind users to start recording when canvassing
   * Always - User gets a pop-up anytime they are actively canvassing and the recorder is not on
   * Daily - User gets a reminder the first time they are actively canvassing for the day
   * Never - No reminder to turn on recorder
   * @defaultValue 'Never'
   */
  recordingReminder: RecordingReminderPreferenceSchema.optional(),
  unitSystem: UnitSystemPreferenceSchema.optional(),
  pinnedTabs: z.array(z.string()).max(3).optional(),
  favoritedFeatures: z.array(z.string()).optional(),
  accountListViewOrder: ViewOrderSchema.optional(),
  areaManagementViewOrder: ViewOrderSchema.optional(),
  hiddenCanvassMapLayers: z.array(HiddenCanvassMapLayerSchema).optional(),
  hiddenCanvassUpsells: z.array(HiddenCanvassUpsellSchema).optional(),
  lockedUserFields: z.array(LockableUserFieldSchema).optional(),
  lockedTeamFields: z.array(LockableTeamFieldSchema).optional(),
  updateAvailabilityTask: z.number().int().optional(),
  /**
   * Controls what stages are shown on the customer layer on canvass
   */
  customerMap: z.object({ workflowId: WorkflowIdSchema, workflowStageId: WorkflowStageIdSchema }).array().optional(),
})
export type Preferences = z.infer<typeof PreferencesSchema>

export const PREFERENCE_KEYS = Object.keys(PreferencesSchema.shape) as Preference[]

export const RequiredPreferencesSchema = PreferencesSchema.required()

export type DefinedPreferences<T extends Preference> = NonNullable<Preferences[T]>

export const PreferenceSchema = PreferencesSchema.keyof()
export type Preference = z.infer<typeof PreferenceSchema>
export const isPreference = (value: unknown): value is Preference => PreferenceSchema.safeParse(value).success

export const DEFAULT_PREFERENCES: Required<Preferences> = {
  customerMap: [],
  closerStatus: 'Can_Close',
  closerModel: 'setter',
  systemCloserStrategy: { type: 'random' },
  country: 'US',
  blockCustomLocationDetailEdits: false,
  bookingPercent: 100,
  requireClockInBeforeContact: false,
  allowDuplicateAccounts: 'Allow',
  secondsBetweenPitches: 90,
  metersBetweenPitchesToday: 10,
  maxMetersFromLocationToActivity: 1000,
  maxMetersFromLocationToPitch: 250,
  maxMetersFromEventToDisposition: 0,
  leaderboardColumns: [],
  defaultStatsColumns: ['contact.pitches'], // TODO: migrate this?
  phonePitch: false,
  limitByArea: false,
  taskLockout: false,
  planLockout: false,
  restrictSetterCloseEvents: false,
  requireEvalToCanvass: false,
  autoAssignEvals: false,
  closerInteraction: 'Full',
  requireLeadResidentInfo: true,
  showCommercialProperties: false,
  maxMinutesSetterCanDeleteEvent: 60,
  areaVisibility: 'Self',
  enerflo: false,
  sso: false,
  showMultiUnitDwellings: false,
  hideInactivePins: false,
  enableVisibilityRestrictions: false,
  meetingDuration: 60,
  timeBetweenAppointments: 0,
  closerSettings: {},
  daysBetweenPitch: -1,
  daysBetweenInterview: 90,
  daysBetweenShadow: 90,
  canBeInMultipleTeams: false,
  managerBasedTeams: false,
  daysBetweenAskManager: -1,
  dispositionMode: 'canvass',
  conversationExpiration: 0,
  multiLanguage: false,
  inactiveDaysBeforeArchive: 0,
  enableAreaManagement: true,
  strictSubareaAssignment: 'off',
  enableAccountManagement: false,
  conversationLocationUpdateThreshold: 100,
  recordingReminder: 'Never',
  unitSystem: 'imperial',
  pinnedTabs: ['canvass', 'calendar'],
  favoritedFeatures: [],
  accountListViewOrder: [],
  areaManagementViewOrder: [],
  hiddenCanvassMapLayers: [],
  hiddenCanvassUpsells: [],
  lockedUserFields: [],
  lockedTeamFields: [],
  updateAvailabilityTask: -1,
}

export const STAFF_ONLY_PREFERENCES = [
  'enableAccountManagement',
  'enableAreaManagement',
  'managerBasedTeams',
  'hiddenCanvassMapLayers',
  'hiddenCanvassUpsells',
] as const satisfies readonly Preference[]

export type StaffOnlyPreference = (typeof STAFF_ONLY_PREFERENCES)[number]
export const isStaffOnlyPreference = (value: unknown): value is StaffOnlyPreference =>
  STAFF_ONLY_PREFERENCES.some((pref) => pref === value)

export const COMPANY_PREFERENCES: Preference[] = [
  'country',
  'blockCustomLocationDetailEdits',
  'allowDuplicateAccounts',
  'areaVisibility',
  'autoAssignEvals',
  'bookingPercent',
  'closerModel',
  'systemCloserStrategy',
  'closerStatus',
  'limitByArea',
  'maxMetersFromLocationToActivity',
  'maxMetersFromLocationToPitch',
  'maxMetersFromEventToDisposition',
  'metersBetweenPitchesToday',
  'phonePitch',
  'requireClockInBeforeContact',
  'requireEvalToCanvass',
  'requireLeadResidentInfo',
  'restrictSetterCloseEvents',
  'secondsBetweenPitches',
  'closerInteraction',
  'leaderboardColumns',
  'defaultStatsColumns',
  'showCommercialProperties',
  'maxMinutesSetterCanDeleteEvent',
  'country',
  'showMultiUnitDwellings',
  'hideInactivePins',
  'enableVisibilityRestrictions',
  'meetingDuration',
  'timeBetweenAppointments',
  'closerSettings',
  'daysBetweenPitch',
  'daysBetweenInterview',
  'daysBetweenShadow',
  'taskLockout',
  'planLockout',
  'canBeInMultipleTeams',
  'managerBasedTeams',
  'daysBetweenAskManager',
  'dispositionMode',
  'conversationExpiration',
  'multiLanguage',
  'inactiveDaysBeforeArchive',
  'enableAccountManagement',
  'enableAreaManagement',
  'strictSubareaAssignment',
  'conversationLocationUpdateThreshold',
  'recordingReminder',
  'unitSystem',
  'pinnedTabs',
  'hiddenCanvassMapLayers',
  'hiddenCanvassUpsells',
  'lockedUserFields',
  'lockedTeamFields',
  'updateAvailabilityTask',
] as const
export type CompanyPreferences = (typeof COMPANY_PREFERENCES)[number]

export const TEAM_PREFERENCES = [
  'closerStatus',
  'closerModel',
  'systemCloserStrategy',
  'areaVisibility',
  'restrictSetterCloseEvents',
  'bookingPercent',
  'maxMetersFromLocationToPitch',
  'maxMetersFromEventToDisposition',
  'secondsBetweenPitches',
  'metersBetweenPitchesToday',
  'meetingDuration',
  'timeBetweenAppointments',
  'closerSettings',
  'closerInteraction',
  'leaderboardColumns',
  'daysBetweenInterview',
  'daysBetweenShadow',
  'dispositionMode',
  'enableAccountManagement',
  'enableAreaManagement',
  'strictSubareaAssignment',
  'conversationLocationUpdateThreshold',
  'planLockout',
  'pinnedTabs',
] as const satisfies readonly Preference[]

export const PITCH_DEPRECATED_PREFERENCES: PitchDeprecatedPreference[] = [
  'maxMetersFromLocationToPitch',
  'secondsBetweenPitches',
  'metersBetweenPitchesToday',
  'phonePitch',
  'daysBetweenPitch',
  'maxMetersFromEventToDisposition',
  'maxMetersFromLocationToActivity',
] as const
export type PitchDeprecatedPreference = Extract<
  Preference,
  | 'maxMetersFromLocationToPitch'
  | 'secondsBetweenPitches'
  | 'metersBetweenPitchesToday'
  | 'phonePitch'
  | 'daysBetweenPitch'
  | 'maxMetersFromEventToDisposition'
  | 'maxMetersFromLocationToActivity'
>

export type TeamPreference = (typeof TEAM_PREFERENCES)[number]
export const isTeamPreference = (value: unknown): value is TeamPreference =>
  TEAM_PREFERENCES.some((pref) => pref === value)

export type TeamPreferences = Pick<Preferences, TeamPreference>

export const USER_PREFERENCES = ['favoritedFeatures', 'accountListViewOrder', 'areaManagementViewOrder'] as const
export type UserPreference = (typeof USER_PREFERENCES)[number]
export const isUserPreference = (value: unknown): value is UserPreference =>
  USER_PREFERENCES.some((pref) => pref === value)

export const preferenceValueSchema = <T extends keyof Preferences>(key: T): (typeof PreferencesSchema.shape)[T] =>
  PreferencesSchema.shape[key]

export type PitchPreferences = Pick<
  Preferences,
  'maxMetersFromLocationToPitch' | 'secondsBetweenPitches' | 'metersBetweenPitchesToday' | 'phonePitch' | 'unitSystem'
>

export const PreferenceInheritOutputGenericSchema = z.object({
  scopeId: EntityIdSchema,
  preferenceId: PreferenceSchema,
  value: z.union(Object.values(RequiredPreferencesSchema.shape)),
  inherited: z.union(Object.values(RequiredPreferencesSchema.shape)),
  current: z.union(Object.values(PreferencesSchema.shape)).optional(),
})

export type PreferenceInheritOutput<T extends Preference> = Omit<PreferenceData<T>, 'value'> & {
  /** current or inherited preference */
  value: DefinedPreferences<T>
  inherited: DefinedPreferences<T>
  /** the preference set by this entity, if any */
  current?: Preferences[T]
}

export type PreferenceData<T extends Preference> = AuditProps & {
  scopeId: EntityId
  preferenceId: T
  value: Preferences[T]
}

export type PreferenceEntity<T extends Preference> = {
  entityId: EntityId
  preference: PreferenceInheritOutput<T>
}

export type PreferenceOutput<T extends Preference> = Pick<Required<Preferences>, T>
